import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import type { Booking, Lesson } from '@court-sync/shared';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Badge } from '@/components/ui/Badge';
import { Loader2 } from 'lucide-react';
import { bookingService } from '@/services/booking';
import { lessonService } from '@/services/lesson';

export interface BookingDetailDialogProps {
  bookingId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  studentDisplayName?: string;
}

export function BookingDetailDialog({
  bookingId,
  open,
  onOpenChange,
  studentDisplayName,
}: BookingDetailDialogProps) {
  const [booking, setBooking] = useState<Booking | null>(null);
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !bookingId) {
      setBooking(null);
      setLesson(null);
      setError(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const res = await bookingService.getBooking(bookingId);
        if (!res.success || !res.data) {
          throw new Error((res as { error?: string }).error || 'Booking not found');
        }
        const b = res.data;
        if (cancelled) return;
        setBooking(b);
        const lessonRes = await lessonService.getLesson(b.lessonId);
        if (!cancelled) {
          setLesson(lessonRes.data ?? null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load booking');
          setBooking(null);
          setLesson(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, bookingId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Booking details</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden />
            Loading…
          </div>
        ) : error ? (
          <p className="text-sm text-destructive py-4">{error}</p>
        ) : booking ? (
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="text-muted-foreground">Lesson</dt>
              <dd className="font-medium">{lesson?.name ?? booking.lessonId}</dd>
            </div>
            {studentDisplayName ? (
              <div>
                <dt className="text-muted-foreground">Student</dt>
                <dd className="font-medium">{studentDisplayName}</dd>
              </div>
            ) : null}
            <div>
              <dt className="text-muted-foreground">Status</dt>
              <dd>
                <Badge variant={booking.status === 'confirmed' ? 'success' : 'secondary'}>
                  {booking.status}
                </Badge>
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Payment</dt>
              <dd className="capitalize">{booking.paymentStatus}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Amount</dt>
              <dd className="font-semibold tabular-nums">${booking.totalAmount}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Booked</dt>
              <dd className="tabular-nums">
                {format(new Date(booking.createdAt), 'd MMM yyyy, h:mm a')}
              </dd>
            </div>
            {booking.sessionDate ? (
              <div>
                <dt className="text-muted-foreground">Session date</dt>
                <dd>{format(new Date(`${booking.sessionDate.split('T')[0]}T12:00:00`), 'EEE d MMM yyyy')}</dd>
              </div>
            ) : null}
            {lesson ? (
              <div>
                <dt className="text-muted-foreground">Schedule</dt>
                <dd>
                  {lesson.startTime}–{lesson.endTime}
                  {lesson.daysOfWeek?.length
                    ? ` · ${lesson.daysOfWeek.map((d) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d]).join(', ')}`
                    : ''}
                </dd>
              </div>
            ) : null}
          </dl>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
